import { classifyItem } from './classificationEngine'
import { LLM_PRIMARY_MODE_ENABLED, logClassificationFallback } from './config'
import { supabase } from './supabase'

interface LLMClassificationResult {
  category_id: string | null
  category_name: string | null
  subcategory_id: string | null
  subcategory_name: string | null
  confidence: 'high' | 'medium' | 'low' | 'none'
  tags: {
    tag_id: string
    tag_name: string
    matched_ruleset: string
    confidence: number
  }[]
}

/**
 * Looks up classification IDs from names (for when classification was done during scraping)
 */
async function lookupClassificationIds(
  categoryName: string,
  subcategoryName: string | undefined,
  tagNames: string[],
  llmConfidence: number | undefined
): Promise<LLMClassificationResult | null> {
  try {
    // Look up category ID from name
    let categoryId: string | null = null
    if (categoryName) {
      const { data: categoryData } = await supabase
        .from('categories')
        .select('id')
        .eq('name', categoryName)
        .single()
      categoryId = categoryData?.id || null
    }

    if (!categoryId) {
      console.error('Category not found:', categoryName)
      return null
    }

    // Look up subcategory ID from name
    let subcategoryId: string | null = null
    if (subcategoryName && categoryId) {
      const { data: subcategoryData } = await supabase
        .from('subcategories')
        .select('id')
        .eq('name', subcategoryName)
        .eq('category_id', categoryId)
        .single()
      subcategoryId = subcategoryData?.id || null
    }

    // Look up tag IDs from names
    const tags: LLMClassificationResult['tags'] = []
    if (tagNames && tagNames.length > 0) {
      const { data: tagData } = await supabase
        .from('tags')
        .select('id, name')
        .in('name', tagNames)

      if (tagData) {
        for (const tag of tagData) {
          tags.push({
            tag_id: tag.id,
            tag_name: tag.name,
            matched_ruleset: 'LLM',
            confidence: llmConfidence || 0.9
          })
        }
      }
    }

    // Map LLM confidence (0-1) to our confidence levels
    let confidence: 'high' | 'medium' | 'low' | 'none' = 'medium'
    if (llmConfidence !== undefined) {
      if (llmConfidence >= 0.8) confidence = 'high'
      else if (llmConfidence >= 0.5) confidence = 'medium'
      else if (llmConfidence > 0) confidence = 'low'
      else confidence = 'none'
    }

    return {
      category_id: categoryId,
      category_name: categoryName,
      subcategory_id: subcategoryId,
      subcategory_name: subcategoryName || null,
      confidence,
      tags
    }
  } catch (error) {
    console.error('Error looking up classification IDs:', error)
    return null
  }
}

/**
 * Calls the LLM Edge Function and waits for results
 * Returns classification in the same format as rule-based for compatibility
 */
async function classifyWithLLM(
  contributionId: string,
  title: string,
  brand: string | undefined,
  description: string
): Promise<LLMClassificationResult | null> {
  try {
    const { data, error } = await supabase.functions.invoke('classify-llm', {
      body: { contribution_id: contributionId, title, brand, description }
    })

    if (error || !data?.success) {
      console.error('LLM classification failed:', error || data?.error)
      return null
    }

    // Look up category ID from name
    let categoryId: string | null = null
    if (data.category) {
      const { data: categoryData } = await supabase
        .from('categories')
        .select('id')
        .eq('name', data.category)
        .single()
      categoryId = categoryData?.id || null
    }

    // Look up subcategory ID from name
    let subcategoryId: string | null = null
    if (data.subcategory && categoryId) {
      const { data: subcategoryData } = await supabase
        .from('subcategories')
        .select('id')
        .eq('name', data.subcategory)
        .eq('category_id', categoryId)
        .single()
      subcategoryId = subcategoryData?.id || null
    }

    // Look up tag IDs from names
    const tags: LLMClassificationResult['tags'] = []
    if (data.tags && data.tags.length > 0) {
      const { data: tagData } = await supabase
        .from('tags')
        .select('id, name')
        .in('name', data.tags)

      if (tagData) {
        for (const tag of tagData) {
          tags.push({
            tag_id: tag.id,
            tag_name: tag.name,
            matched_ruleset: 'LLM',
            confidence: data.confidence || 0.9
          })
        }
      }
    }

    // Map LLM confidence (0-1) to our confidence levels
    let confidence: 'high' | 'medium' | 'low' | 'none' = 'medium'
    if (data.confidence >= 0.8) confidence = 'high'
    else if (data.confidence >= 0.5) confidence = 'medium'
    else if (data.confidence > 0) confidence = 'low'
    else confidence = 'none'

    return {
      category_id: categoryId,
      category_name: data.category || null,
      subcategory_id: subcategoryId,
      subcategory_name: data.subcategory || null,
      confidence,
      tags
    }
  } catch (error) {
    console.error('LLM classification error:', error)
    return null
  }
}

// Simplest possible contribution insert - bypassing everything
export async function insertContributionDirect(contribution: any, userId: string, accessToken: string) {
  const url = process.env.REACT_APP_SUPABASE_URL!
  const key = process.env.REACT_APP_SUPABASE_ANON_KEY!

  // Extract temporary fields and category names that shouldn't go to contributions table
  // (categories are stored in item_classifications table, not as enum in contributions)
  // Also extract computed fields like is_saved that come from joins, not actual columns
  const {
    // Internal/temporary fields
    _classification,
    _skipAutoClassify,
    _selectedCategoryId,
    _selectedSubcategoryId,
    // Classification fields (stored in item_classifications table)
    category,
    subcategory,
    category_name,
    subcategory_name,
    llm_confidence,
    // Computed fields from joins
    is_saved,
    tag_names,
    // Fields not in contributions table
    price,
    ...cleanContribution
  } = contribution

  const payload: any = {
    ...cleanContribution,
    user_id: userId,
    is_active: true,
    saves_count: 0,
    views_count: 0
  }

  // Remove id if null/undefined - let database auto-generate UUID
  if (!payload.id) {
    delete payload.id
  }

  try {
    // Use basic fetch with abort controller
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 5000)

    const response = await fetch(`${url}/rest/v1/contributions`, {
      method: 'POST',
      headers: {
        'apikey': key,
        'Authorization': `Bearer ${accessToken}`, // Use user's access token, not anon key!
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify(payload),
      signal: controller.signal
    })

    clearTimeout(timeoutId)

    const text = await response.text()

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${text}`)
    }

    const responseData = JSON.parse(text)

    // Supabase returns an array when using Prefer: return=representation
    const insertedItem = Array.isArray(responseData) ? responseData[0] : responseData

    if (!insertedItem || !insertedItem.id) {
      throw new Error('Failed to get inserted item ID')
    }

    // Handle classification and tags
    let classificationToSave = null

    // Check if we already have classification data from scraping (with IDs)
    if (_skipAutoClassify && _classification) {
      classificationToSave = _classification
    }
    // Check if we have category/subcategory/tags names from LLM scraping (need to look up IDs)
    else if (LLM_PRIMARY_MODE_ENABLED && category) {
      try {
        classificationToSave = await lookupClassificationIds(
          category,
          subcategory,
          cleanContribution.tags || [],
          cleanContribution.llm_confidence
        )
      } catch (lookupError) {
        console.error('Failed to lookup classification IDs:', lookupError)
        // Fall through to re-classify
      }
    }

    // If no classification yet, auto-classify the item after insertion
    if (!classificationToSave) {
      if (LLM_PRIMARY_MODE_ENABLED) {
        try {
          classificationToSave = await classifyWithLLM(
            insertedItem.id,
            insertedItem.title || '',
            insertedItem.brand,
            insertedItem.description || ''
          )

          if (!classificationToSave) {
            // LLM classification returned null, fall back to rule-based
            logClassificationFallback('llm_null_result', insertedItem.id)
            classificationToSave = await classifyItem(
              insertedItem.title || '',
              insertedItem.description || '',
              insertedItem.source_url || '',
              {
                contributionId: insertedItem.id,
                userId,
                brand: insertedItem.brand
              }
            )
          }
        } catch (classifyError) {
          // LLM threw an error, fall back to rule-based
          logClassificationFallback('llm_error', insertedItem.id, classifyError)
          try {
            classificationToSave = await classifyItem(
              insertedItem.title || '',
              insertedItem.description || '',
              insertedItem.source_url || '',
              {
                contributionId: insertedItem.id,
                userId,
                brand: insertedItem.brand
              }
            )
          } catch (fallbackError) {
            console.error('Rule-based fallback also failed:', fallbackError)
          }
        }
      } else {
        // Rule-based classification (original behavior)
        try {
          classificationToSave = await classifyItem(
            insertedItem.title || '',
            insertedItem.description || '',
            insertedItem.source_url || '',
            {
              contributionId: insertedItem.id,
              userId,
              brand: insertedItem.brand
            }
          )
        } catch (classifyError) {
          // Don't fail the whole operation if classification fails
        }
      }
    }

    // Save classification to database if we have it
    if (classificationToSave && classificationToSave.category_id) {
      try {
        const { error: classError } = await supabase
          .from('item_classifications')
          .insert({
            contribution_id: insertedItem.id,
            category_id: classificationToSave.category_id,
            subcategory_id: classificationToSave.subcategory_id,
            confidence_score: classificationToSave.confidence === 'high' ? 0.95 : classificationToSave.confidence === 'medium' ? 0.8 : 0.6,
            needs_review: classificationToSave.confidence === 'low'
          })

        if (classError) {
          console.error('Failed to save classification:', classError)
        }
      } catch (classError) {
        console.error('Error saving classification:', classError)
      }
    }

    // Save tags to database if we have them
    if (classificationToSave && classificationToSave.tags && classificationToSave.tags.length > 0) {
      try {
        // If tags don't have IDs (only names), look them up first
        let tagsWithIds = classificationToSave.tags
        const needsIdLookup = classificationToSave.tags.some((tag: any) => !tag.tag_id && tag.tag_name)

        if (needsIdLookup) {
          const tagNames = classificationToSave.tags
            .filter((tag: any) => tag.tag_name)
            .map((tag: any) => tag.tag_name)

          if (tagNames.length > 0) {
            // Use case-insensitive matching by comparing lowercase names
            const lowerTagNames = tagNames.map((n: string) => n.toLowerCase())

            const { data: tagData } = await supabase
              .from('tags')
              .select('id, name')

            if (tagData) {
              // Match tags case-insensitively
              const matchedTags = tagData.filter(tag =>
                lowerTagNames.includes(tag.name.toLowerCase())
              )

              tagsWithIds = matchedTags.map(tag => ({
                tag_id: tag.id,
                tag_name: tag.name
              }))

              // Log any tags that weren't found in the database
              const foundTagNamesLower = new Set(matchedTags.map(t => t.name.toLowerCase()))
              const missingTags = tagNames.filter((t: string) => !foundTagNamesLower.has(t.toLowerCase()))

              if (missingTags.length > 0) {
                console.warn(
                  `[Contribution ${insertedItem.id}] Tags not found in database (discarded):`,
                  missingTags.join(', ')
                )
              }
            }
          }
        }

        // Filter out any tags that still don't have IDs
        const validTags = tagsWithIds.filter((tag: any) => tag.tag_id)

        if (validTags.length > 0) {
          const tagInserts = validTags.map((tag: any) => ({
            contribution_id: insertedItem.id,
            tag_id: tag.tag_id,
            auto_assigned: true
          }))

          const { error: tagsError } = await supabase
            .from('item_tags')
            .insert(tagInserts)

          if (tagsError) {
            console.error('Failed to save tags:', tagsError)
          }
        }
      } catch (tagsError) {
        console.error('Error saving tags:', tagsError)
      }
    }

    // Ensure brand exists in brands table
    if (insertedItem.brand && insertedItem.brand.trim()) {
      try {
        const brandName = insertedItem.brand.trim()

        // Generate slug from brand name (same logic as database function)
        const brandSlug = brandName.toLowerCase().replace(/[^a-z0-9]+/g, '-')

        // Insert brand (will be ignored if already exists due to UNIQUE constraint)
        const { error: brandError } = await supabase
          .from('brands')
          .insert({
            name: brandName,
            slug: brandSlug
          })
          .select()
          .single()

        // Error 23505 is "duplicate key value violates unique constraint" - expected when brand exists
        if (brandError && brandError.code !== '23505') {
          console.error('Failed to create brand:', brandError)
        }

        // Update contribution with brand_id
        const { data: brand } = await supabase
          .from('brands')
          .select('id')
          .eq('slug', brandSlug)
          .single()

        if (brand) {
          await supabase
            .from('contributions')
            .update({ brand_id: brand.id })
            .eq('id', insertedItem.id)
        }
      } catch (brandError) {
        // Don't fail the whole operation if brand creation fails
        console.error('Error ensuring brand:', brandError)
      }
    }

    return insertedItem
  } catch (error: any) {
    if (error.name === 'AbortError') {
      console.error('Request timed out after 5 seconds')
    } else {
      console.error('Fetch error:', error)
    }
    throw error
  }
}